import { useAccountStore } from "@/features/account-management";
import { openProDialog } from "@/shared";
import { TGetAnalyticsData, analyticsApi } from "@/shared/services";
import Spinner from "@components/ui/spinner";
import { ANALYTICS_QUERY, type CsvType } from "@pages/analytics/types";
import { parseDataToCsv } from "@pages/analytics/utils";
import { IconFileTypeCsv } from "@tabler/icons-react";
import moment from "moment";
import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import useGetQueryData from "../../hooks/use-get-query-data";

export const SessionsExportCsv = ({
  overrides,
}: {
  overrides: Partial<TGetAnalyticsData["params"]>;
}) => {
  const params = useGetQueryData(true); // Skip debounce for export
  const [isLoading, setIsLoading] = useState(false);
  const showPlaceholder = useAccountStore((s) => s.showAnalyticsPlaceholder);
  const isLocked = useAccountStore(
    (s) => s.planType === "trial" || s.planType === "starter",
  );

  const handleExport = useCallback(async () => {
    if (isLocked) return openProDialog("analytics | export csv");
    if (isLoading) return;

    setIsLoading(true);
    try {
      const query = [ANALYTICS_QUERY.CSV];
      const data = await analyticsApi.getAnalyticsData({
        query,
        params: { ...params, ...overrides, demo: showPlaceholder },
      });

      const sessions = (data?.[ANALYTICS_QUERY.CSV] || []) as CsvType[];
      const csv = parseDataToCsv(sessions);

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Storydoc sessions ${moment().local().format("DD-MM-YYYY")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export CSV");
    }
    setIsLoading(false);
  }, [params, isLoading]);

  return (
    <button
      type="button"
      onClick={handleExport}
      disabled={isLoading}
      className="tw-flex tw-items-center tw-gap-1 tw-outline-1 tw-text-blue hover:tw-bg-blue-50 tw-transition-colors tw-text-sm tw-font-sans tw-font-medium tw-outline-blue-100 tw-outline tw-rounded-lg tw-bg-white tw-h-9 tw-px-3.5 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed -xl:tw-hidden"
    >
      {isLoading ? (
        <Spinner className="tw-size-4 tw-border-blue" />
      ) : (
        <IconFileTypeCsv />
      )}
      Export CSV
    </button>
  );
};
